import { useMutation, useQuery } from '@tanstack/react-query';

import reviewQuestionnaireService from './reviewQuestionnaireService';

import type { ImportTemplateParams, UseGetReviewQuestionnaireParams } from './types';
import type { ReviewQuestionnaire } from '@/types/review/ReviewQuestionnaire';

import { queryClient } from '@/AppWithProviders';
import {
    ReviewQueryKey,
    ReviewQuestionnaireMutationKey,
    ReviewQuestionnaireQueryKey,
} from '@/constants/QueryKeys';
import { useCancellableMutationFn } from '@/hooks/api/useCancellableMutationFn';

const useGetReviewQuestionnaire = ({
    reviewQuestionnaireId,
    options,
}: UseGetReviewQuestionnaireParams) => {
    return useQuery<ReviewQuestionnaire>({
        queryKey: [ReviewQuestionnaireQueryKey.REVIEW_QUESTIONNAIRE, reviewQuestionnaireId],
        queryFn: () => reviewQuestionnaireService.getReviewQuestionnaire(reviewQuestionnaireId!),
        enabled: !!reviewQuestionnaireId,
        ...options,
    });
};

const useUpdateReviewQuestionnaire = () => {
    const cancellableMutationFn = useCancellableMutationFn(
        reviewQuestionnaireService.updateReviewQuestionnaire,
    );

    return useMutation({
        mutationKey: [ReviewQuestionnaireMutationKey.REVIEW_QUESTIONNAIRE],
        mutationFn: cancellableMutationFn,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
        },
    });
};

const useImportTemplateInQuestionnaire = () => {
    return useMutation({
        mutationFn: (payload: ImportTemplateParams) =>
            reviewQuestionnaireService.importTemplate(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
        },
    });
};

export {
    useGetReviewQuestionnaire,
    useUpdateReviewQuestionnaire,
    useImportTemplateInQuestionnaire,
};
